// Core packages
import { Injectable } from "@angular/core";
import { FormGroup } from "@angular/forms";

// Third party packages
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

// Custom packages
import { ApiResponse } from "src/app/shared/interfaces/_base/api-response.interface";
import {
  DocumentTemplateArea,
  DocumentTemplateAreaCreatePayload,
  DocumentTemplateAreaUpdatePayload,
  DocumentTemplateContent,
  DocumentTemplateContentCreatePayload,
  DocumentTemplateContentUpdatePayload,
  DocumentTemplateDeleteResult,
  DocumentTemplateDoc,
  DocumentTemplateDocCreatePayload,
  DocumentTemplateDocUpdatePayload,
  DocumentTemplateStructureOption,
  DocumentTemplateTree,
} from "src/app/shared/interfaces/document-template.interface";
import { ApiService } from "src/app/shared/services/api.service";
import { HelperService } from "src/app/shared/services/helper.service";

/** Base path of the three-collections document template API */
const BASE_PATH = "document-template";

/** The whole API is v2-only: force it regardless of the environment default */
const API_VERSION = "v2";

/**
 * Options driving how an API error is surfaced to the user.
 *
 * @since 3.0.0
 */
export interface DocumentTemplateErrorOptions {
  /** Form the field level errors must be attached to */
  form?: FormGroup;
  /**
   * Controls that could be the duplicated one on a 409. The back-end message is
   * deliberately generic, so the page tells which fields it actually sent and every
   * one of them gets highlighted.
   */
  conflictFields?: string[];
  /** Called on 404, to let the caller reload the stale list */
  onNotFound?: () => void;
}

/**
 * Front-end API layer of the document template (three collections: areas, contents,
 * docs). Every route requires the `canCreateSettings` permission, reads included.
 *
 * @since 3.0.0
 */
@Injectable({
  providedIn: "root",
})
export class DocumentTemplateService {
  /**
   * Class constructor
   */
  constructor(
    private apiService: ApiService,
    private helperService: HelperService,
    private toastrService: ToastrService,
    private translateService: TranslateService
  ) {}

  // BEGIN - Tree

  /**
   * Whole template in one call.
   *
   * NOT the source of the UI (the page loads one level at a time): it is used for the
   * global preview and to count what a cascading delete is about to hide.
   *
   * @since 3.0.0
   *
   * @param [includeDeleted] When true, hidden nodes are returned too
   */
  getTree(includeDeleted?: boolean): Observable<ApiResponse<DocumentTemplateTree>> {
    return this.call("GET", `${BASE_PATH}/tree`, {
      includeDeleted: includeDeleted ? "true" : "false",
    });
  }

  // END - Tree

  // BEGIN - Level 1: areas

  /**
   * List the areas, ordered by position.
   *
   * @since 3.0.0
   *
   * @param [includeDeleted] When true, hidden areas are returned too
   */
  listAreas(
    includeDeleted?: boolean
  ): Observable<ApiResponse<DocumentTemplateArea[]>> {
    return this.call("GET", `${BASE_PATH}/areas`, {
      includeDeleted: includeDeleted ? "true" : "false",
    });
  }

  /**
   * Get a single area.
   *
   * @since 3.0.0
   */
  getArea(areaId: string): Observable<ApiResponse<DocumentTemplateArea>> {
    return this.call("GET", `${BASE_PATH}/areas/${areaId}`);
  }

  /**
   * Create an area. The back-end appends it at the end of the level.
   *
   * @since 3.0.0
   */
  createArea(
    payload: DocumentTemplateAreaCreatePayload
  ): Observable<ApiResponse<DocumentTemplateArea>> {
    return this.call("POST", `${BASE_PATH}/areas`, payload);
  }

  /**
   * Update an area. The payload is partial by design: send only what changes.
   * `deleted` is not accepted and would be ignored.
   *
   * @since 3.0.0
   */
  updateArea(
    areaId: string,
    payload: DocumentTemplateAreaUpdatePayload
  ): Observable<ApiResponse<DocumentTemplateArea>> {
    return this.call("PUT", `${BASE_PATH}/areas/${areaId}`, payload);
  }

  /**
   * Soft delete an area, CASCADING on its sections and their documents.
   *
   * @since 3.0.0
   */
  deleteArea(
    areaId: string
  ): Observable<ApiResponse<DocumentTemplateDeleteResult>> {
    return this.call("DELETE", `${BASE_PATH}/areas/${areaId}`);
  }

  /**
   * Restore an area. NOT cascading: its sections and documents stay hidden and have
   * to be restored one by one.
   *
   * @since 3.0.0
   */
  restoreArea(areaId: string): Observable<ApiResponse<DocumentTemplateArea>> {
    return this.call("PATCH", `${BASE_PATH}/areas/${areaId}/restore`, {});
  }

  // END - Level 1: areas

  // BEGIN - Level 2: contents (sections)

  /**
   * List the sections of an area, ordered by position.
   *
   * @since 3.0.0
   */
  listContents(
    areaId: string,
    includeDeleted?: boolean
  ): Observable<ApiResponse<DocumentTemplateContent[]>> {
    return this.call("GET", `${BASE_PATH}/contents`, {
      areaId,
      includeDeleted: includeDeleted ? "true" : "false",
    });
  }

  /**
   * Get a single section.
   *
   * @since 3.0.0
   */
  getContent(
    contentId: string
  ): Observable<ApiResponse<DocumentTemplateContent>> {
    return this.call("GET", `${BASE_PATH}/contents/${contentId}`);
  }

  /**
   * Create a section under the given area.
   *
   * @since 3.0.0
   */
  createContent(
    payload: DocumentTemplateContentCreatePayload
  ): Observable<ApiResponse<DocumentTemplateContent>> {
    return this.call("POST", `${BASE_PATH}/contents`, payload);
  }

  /**
   * Update a section. Sending only `{ areaId }` moves it to another area, appended at
   * the end of the destination.
   *
   * @since 3.0.0
   */
  updateContent(
    contentId: string,
    payload: DocumentTemplateContentUpdatePayload
  ): Observable<ApiResponse<DocumentTemplateContent>> {
    return this.call("PUT", `${BASE_PATH}/contents/${contentId}`, payload);
  }

  /**
   * Soft delete a section, CASCADING on its documents.
   *
   * @since 3.0.0
   */
  deleteContent(
    contentId: string
  ): Observable<ApiResponse<DocumentTemplateDeleteResult>> {
    return this.call("DELETE", `${BASE_PATH}/contents/${contentId}`);
  }

  /**
   * Restore a section. Answers 400 when its area is still hidden.
   *
   * @since 3.0.0
   */
  restoreContent(
    contentId: string
  ): Observable<ApiResponse<DocumentTemplateContent>> {
    return this.call("PATCH", `${BASE_PATH}/contents/${contentId}/restore`, {});
  }

  // END - Level 2: contents (sections)

  // BEGIN - Level 3: docs

  /**
   * List the documents of a section, ordered by position.
   *
   * @since 3.0.0
   */
  listDocs(
    contentId: string,
    includeDeleted?: boolean
  ): Observable<ApiResponse<DocumentTemplateDoc[]>> {
    return this.call("GET", `${BASE_PATH}/docs`, {
      contentId,
      includeDeleted: includeDeleted ? "true" : "false",
    });
  }

  /**
   * Get a single document.
   *
   * @since 3.0.0
   */
  getDoc(docId: string): Observable<ApiResponse<DocumentTemplateDoc>> {
    return this.call("GET", `${BASE_PATH}/docs/${docId}`);
  }

  /**
   * Create a document under the given section.
   *
   * @since 3.0.0
   */
  createDoc(
    payload: DocumentTemplateDocCreatePayload
  ): Observable<ApiResponse<DocumentTemplateDoc>> {
    return this.call("POST", `${BASE_PATH}/docs`, payload);
  }

  /**
   * Update a document. Sending only `{ contentId }` moves it to another section,
   * appended at the end of the destination.
   *
   * @since 3.0.0
   */
  updateDoc(
    docId: string,
    payload: DocumentTemplateDocUpdatePayload
  ): Observable<ApiResponse<DocumentTemplateDoc>> {
    return this.call("PUT", `${BASE_PATH}/docs/${docId}`, payload);
  }

  /**
   * Soft delete a document.
   *
   * @since 3.0.0
   */
  deleteDoc(
    docId: string
  ): Observable<ApiResponse<DocumentTemplateDeleteResult>> {
    return this.call("DELETE", `${BASE_PATH}/docs/${docId}`);
  }

  /**
   * Restore a document. Answers 400 when its section is still hidden.
   *
   * @since 3.0.0
   */
  restoreDoc(docId: string): Observable<ApiResponse<DocumentTemplateDoc>> {
    return this.call("PATCH", `${BASE_PATH}/docs/${docId}/restore`, {});
  }

  // END - Level 3: docs

  // BEGIN - Condo structures

  /**
   * Condo structures available as `structure` of an "array" section.
   * Endpoint left untouched by the three-collections refactor.
   *
   * @since 3.0.0
   */
  getCondoStructures(): Observable<any> {
    return this.call("GET", "condo-structures/settings");
  }

  /**
   * Flatten the condo-structures settings payload into the list the "structure"
   * select needs. The endpoint still answers with the legacy single-document shape
   * (`condoStructureTemplate[0].area[].content[]`), so both the legacy root and the
   * v2 `data` envelope are accepted.
   *
   * @since 3.0.0
   */
  extractStructureOptions(res: any): DocumentTemplateStructureOption[] {
    const options: DocumentTemplateStructureOption[] = [];
    if (!res) {
      return options;
    }
    const template =
      res.condoStructureTemplate ||
      (res.data ? res.data.condoStructureTemplate : undefined);
    if (!template || !Array.isArray(template) || !template.length) {
      return options;
    }
    const areas = template[0] ? template[0].area : undefined;
    if (!Array.isArray(areas)) {
      return options;
    }
    areas.forEach((area: any) => {
      if (!area || !Array.isArray(area.content)) {
        return;
      }
      area.content.forEach((structure: any) => {
        if (structure && structure.key) {
          options.push({
            key: structure.key,
            translation: structure.translation || { it: structure.key, en: structure.key },
          });
        }
      });
    });
    return options;
  }

  // END - Condo structures

  // BEGIN - Reordering

  /**
   * Reorder a whole level in one atomic call.
   *
   * The three endpoints take the new sequence of ids and answer with the level already
   * sorted, so the front-end neither computes nor sends any `position`.
   *
   * `ids` must list the level EXACTLY as it is being displayed, which is why
   * `includeDeleted` has to match the flag the list was loaded with: with hidden nodes
   * out of the picture the sequence carries only the visible ones.
   *
   * @since 3.0.0
   *
   * @param ids             Area ids in their new order
   * @param [includeDeleted] Same flag the list was loaded with
   */
  reorderAreas(
    ids: string[],
    includeDeleted?: boolean
  ): Observable<ApiResponse<DocumentTemplateArea[]>> {
    return this.call(
      "PATCH",
      `${BASE_PATH}/areas/reorder?includeDeleted=${includeDeleted ? "true" : "false"}`,
      { ids }
    );
  }

  /**
   * Reorder the sections of an area. @see reorderAreas
   *
   * @since 3.0.0
   */
  reorderContents(
    areaId: string,
    ids: string[],
    includeDeleted?: boolean
  ): Observable<ApiResponse<DocumentTemplateContent[]>> {
    return this.call(
      "PATCH",
      `${BASE_PATH}/contents/reorder?includeDeleted=${includeDeleted ? "true" : "false"}`,
      { areaId, ids }
    );
  }

  /**
   * Reorder the documents of a section. @see reorderAreas
   *
   * @since 3.0.0
   */
  reorderDocs(
    contentId: string,
    ids: string[],
    includeDeleted?: boolean
  ): Observable<ApiResponse<DocumentTemplateDoc[]>> {
    return this.call(
      "PATCH",
      `${BASE_PATH}/docs/reorder?includeDeleted=${includeDeleted ? "true" : "false"}`,
      { contentId, ids }
    );
  }

  // END - Reordering

  // BEGIN - Error handling

  /**
   * Surface an API error the way the page needs it:
   *
   *  - 409: duplicated `code`, label or `key`. The back-end message is shown as is: it
   *    names the language that collided and tells an active row apart from a hidden
   *    one, which the front-end cannot always work out on its own. Its own generic
   *    wording is only the fallback, and every field that could hold the duplicate is
   *    highlighted in the form.
   *  - 400: validation failure (or a restore attempted under a hidden parent). The
   *    back-end message is shown as is; field level errors are attached when the
   *    payload carries the legacy `data: { field: { msg, param } }` map, or the
   *    `errors: { field: { path, msg } }` map the v2 validators answer with.
   *  - 404: the node does not exist anymore, the caller reloads its list.
   *  - anything else: standard toastr handling.
   *
   * Never swallows an error silently: something is always shown to the user.
   *
   * @since 3.0.0
   */
  handleApiError(err: any, options?: DocumentTemplateErrorOptions): void {
    const opts = options || {};
    const status = err && typeof err.status !== "undefined" ? err.status : 0;

    if (status === 409) {
      this.applyConflictErrors(opts.form, opts.conflictFields);
      this.toastrService.error(
        this.getBackEndMessage(
          err,
          this.translateService.instant(
            "settings.documentTemplate.errors.conflict"
          )
        ),
        this.translateService.instant("common.words.warning")
      );
      return;
    }

    if (status === 400) {
      if (this.hasFieldErrorMap(err) && opts.form) {
        this.helperService.handleFormlyError(opts.form, err);
        return;
      }
      // The toast is kept even when fields get highlighted: the field may be out of
      // sight, and the `msg` of each entry is the back-end technical wording while
      // `message` is localized
      this.applyValidationErrors(opts.form, err);
      this.toastrService.error(
        this.getBackEndMessage(
          err,
          this.translateService.instant(
            "settings.documentTemplate.errors.invalid"
          )
        ),
        this.translateService.instant("common.words.warning")
      );
      return;
    }

    if (status === 404) {
      this.toastrService.error(
        this.getBackEndMessage(
          err,
          this.translateService.instant(
            "settings.documentTemplate.errors.notFound"
          )
        ),
        this.translateService.instant("common.words.warning")
      );
      if (typeof opts.onNotFound === "function") {
        opts.onNotFound();
      }
      return;
    }

    this.helperService.handleError(err);
  }

  /**
   * Mark every field that could hold the duplicated value, so that the user sees
   * WHERE to act even though the back-end will not say which one it is.
   *
   * @since 3.0.0
   */
  private applyConflictErrors(form: FormGroup, fields: string[]): void {
    if (!form || !Array.isArray(fields) || !fields.length) {
      return;
    }
    const message = this.translateService.instant(
      "settings.documentTemplate.errors.conflictField"
    );
    fields.forEach((field) => {
      const control = form.get(field);
      if (control) {
        control.markAsTouched();
        control.markAsDirty();
        control.setErrors({ "server-error": message });
      }
    });
  }

  /**
   * Attach the entries of a v2 validation `errors` map to the controls they name, e.g.
   * `{ description: { path: "description", msg: "..." } }`.
   *
   * Entries naming no control of the form are skipped: the caller always shows the
   * back-end message too, so nothing gets lost. The shared response envelope types the
   * map as plain strings while the validators send objects: both are accepted.
   *
   * @since 3.0.0
   */
  private applyValidationErrors(form: FormGroup | undefined, err: any): void {
    const errors = err && err.error ? err.error.errors : undefined;
    if (!form || !errors || typeof errors !== "object" || Array.isArray(errors)) {
      return;
    }
    Object.keys(errors).forEach((key) => {
      const entry = errors[key];
      const msg =
        typeof entry === "string"
          ? entry
          : entry && typeof entry.msg === "string"
          ? entry.msg
          : "";
      const path =
        entry && typeof entry.path === "string" && entry.path ? entry.path : key;
      const control = form.get(path);
      if (!msg || !control) {
        return;
      }
      control.markAsTouched();
      control.markAsDirty();
      control.setErrors({ "server-error": msg });
    });
  }

  /**
   * True when the error carries the `{ field: { msg, param } }` map that
   * `handleFormlyError` expects. Guards against the known case of a `data` payload
   * that is a plain result object: forwarding it would produce an empty toast and
   * lose the real message.
   *
   * @since 3.0.0
   */
  private hasFieldErrorMap(err: any): boolean {
    const data = err && err.error ? err.error.data : undefined;
    if (!data || typeof data !== "object" || Array.isArray(data)) {
      return false;
    }
    const keys = Object.keys(data);
    if (!keys.length) {
      return false;
    }
    return keys.every(
      (key) =>
        data[key] &&
        typeof data[key] === "object" &&
        typeof data[key].msg === "string"
    );
  }

  /**
   * Back-end message of an error response, with a fallback.
   *
   * @since 3.0.0
   */
  private getBackEndMessage(err: any, fallback: string): string {
    if (err && err.error && typeof err.error.message === "string" && err.error.message) {
      return err.error.message;
    }
    return fallback;
  }

  // END - Error handling

  /**
   * Thin wrapper around ApiService pinning the v2 version on every call.
   *
   * On a network failure (status 0) ApiService emits an Error as a NEXT value instead
   * of erroring: left as is, the caller would treat it as an empty successful response
   * and silently blank the list. It is turned back into a real error notification
   * here, so that every failure reaches `handleApiError`.
   *
   * @since 3.0.0
   */
  private call(
    method: string,
    path: string,
    params?: any
  ): Observable<any> {
    return this.apiService
      .apiCall(
        method,
        path,
        params,
        undefined,
        undefined,
        undefined,
        API_VERSION
      )
      .pipe(
        map((res: any) => {
          if (res instanceof Error) {
            throw res;
          }
          return res;
        })
      );
  }
}
